import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Button,
  Card,
  CardBody,
  Chip,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Spinner,
  Switch,
  Textarea,
} from '@nextui-org/react';
import {
  AlertTriangle,
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  Calculator,
  CheckCircle2,
  Dices,
  ExternalLink,
  Gift,
  Handshake,
  Image as ImageIcon,
  Pencil,
  Plus,
  Save,
  Shield,
  Ticket,
  Trash2,
  Trophy,
  Upload,
  Users,
  XCircle,
} from 'lucide-react';
import adminPredictionsService, {
  AdminHeroSlide,
  AdminLotteryPrize,
  AdminParticipant,
  AdminPredictionGame,
  AdminPrize,
  AdminSponsor,
} from '../../services/adminPredictionsService';

const ACCENT = '#3fced1';

type TabKey = 'details' | 'participants' | 'slides' | 'prizes' | 'lottery' | 'sponsors';

// ---------- Helpers ----------

const toLocalInput = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fromLocalInput = (value: string): string | null => {
  if (!value.trim()) return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d.toISOString();
};

const formatDateTime = (iso?: string | null): string => {
  if (!iso) return '–';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '–';
  return d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

/**
 * Hinweis-Popup: alle Rückmeldungen (Erfolg/Fehler) werden IMMER zentriert in
 * der Bildschirmmitte als Modal angezeigt (nicht als Inline-Banner im Fluss).
 * Erfolgsmeldungen blenden sich nach 3s automatisch aus.
 */
const FeedbackBanner: React.FC<{
  message: string | null;
  type: 'success' | 'error';
  onClose: () => void;
}> = ({ message, type, onClose }) => {
  const isSuccess = type === 'success';

  useEffect(() => {
    if (!message || !isSuccess) return;
    const timer = window.setTimeout(onClose, 3000);
    return () => window.clearTimeout(timer);
  }, [message, isSuccess, onClose]);

  if (!message) return null;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-sm rounded-2xl border border-zinc-800 bg-zinc-900 p-6 text-center shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={`mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full ${
            isSuccess ? 'bg-[#3fced1]/15' : 'bg-red-500/15'
          }`}
        >
          {isSuccess ? (
            <CheckCircle2 size={28} className="text-[#3fced1]" />
          ) : (
            <XCircle size={28} className="text-red-400" />
          )}
        </div>
        <p className={`text-base font-semibold whitespace-pre-line ${isSuccess ? 'text-[#3fced1]' : 'text-red-400'}`}>
          {message}
        </p>
        <button
          type="button"
          onClick={onClose}
          className="mt-5 w-full rounded-xl bg-[#3fced1] py-2.5 text-sm font-bold text-black"
        >
          OK
        </button>
      </div>
    </div>
  );
};

/** Bild-Feld mit Upload für Modals (Slides/Preise/Sponsoren). */
const ImageField: React.FC<{
  label: string;
  value: string;
  onChange: (url: string) => void;
  onError: (msg: string) => void;
}> = ({ label, value, onChange, onError }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const preview = adminPredictionsService.buildFileUrl(value);

  const handleFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      onError('Bitte eine Bilddatei auswählen (PNG/JPG/WebP).');
      return;
    }
    setUploading(true);
    try {
      const url = await adminPredictionsService.uploadImage(file);
      onChange(url);
    } catch (err) {
      onError(adminPredictionsService.getErrorMessage(err, 'Bild-Upload fehlgeschlagen.'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <p className="mb-1.5 text-sm text-gray-400">{label}</p>
      <div className="flex items-center gap-3">
        <div className="flex h-16 w-28 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-zinc-700 bg-zinc-950">
          {preview ? (
            <img src={preview} alt="" className="h-full w-full object-cover" />
          ) : (
            <ImageIcon size={18} className="text-zinc-700" />
          )}
        </div>
        <Button
          size="sm"
          variant="bordered"
          className="border-zinc-700 text-gray-300"
          isLoading={uploading}
          startContent={!uploading && <Upload size={14} />}
          onPress={() => inputRef.current?.click()}
        >
          {value ? 'Bild ersetzen' : 'Bild hochladen'}
        </Button>
        {value && (
          <Button size="sm" variant="light" className="text-gray-500" onPress={() => onChange('')}>
            Entfernen
          </Button>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
    </div>
  );
};

const inputClasses = {
  inputWrapper: 'bg-zinc-800 border border-zinc-700',
  input: 'text-white',
  label: 'text-gray-400',
};

// =====================================================================
// Generisches Item-Modal (Slides / Preise / Lospreise / Sponsoren)
// =====================================================================

interface ItemField {
  key: string;
  label: string;
  type: 'text' | 'number' | 'textarea' | 'image';
  required?: boolean;
}

const ItemModal: React.FC<{
  isOpen: boolean;
  title: string;
  fields: ItemField[];
  initial: Record<string, any> | null;
  onClose: () => void;
  onSave: (values: Record<string, any>) => Promise<void>;
  onError: (msg: string) => void;
}> = ({ isOpen, title, fields, initial, onClose, onSave, onError }) => {
  const [values, setValues] = useState<Record<string, any>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) setValues(initial ?? {});
  }, [isOpen, initial]);

  const set = (key: string, value: any) => setValues((v) => ({ ...v, [key]: value }));

  const missingRequired = fields.some(
    (f) => f.required && !(String(values[f.key] ?? '').trim().length > 0)
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload: Record<string, any> = {};
      for (const f of fields) {
        const raw = values[f.key];
        if (f.type === 'number') {
          const n = parseInt(String(raw), 10);
          if (!isNaN(n)) payload[f.key] = n;
        } else {
          const s = String(raw ?? '').trim();
          payload[f.key] = s.length > 0 ? s : null;
        }
      }
      await onSave(payload);
      onClose();
    } catch (err) {
      onError(adminPredictionsService.getErrorMessage(err, 'Speichern fehlgeschlagen.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} placement="center" scrollBehavior="inside" size="lg">
      <ModalContent className="border border-zinc-800 bg-zinc-900">
        <ModalHeader className="text-white">{title}</ModalHeader>
        <ModalBody>
          {fields.map((f) => {
            if (f.type === 'image') {
              return (
                <ImageField
                  key={f.key}
                  label={`${f.label}${f.required ? ' *' : ''}`}
                  value={String(values[f.key] ?? '')}
                  onChange={(url) => set(f.key, url)}
                  onError={onError}
                />
              );
            }
            if (f.type === 'textarea') {
              return (
                <Textarea
                  key={f.key}
                  label={f.label}
                  value={String(values[f.key] ?? '')}
                  onValueChange={(v) => set(f.key, v)}
                  minRows={2}
                  classNames={inputClasses}
                />
              );
            }
            return (
              <Input
                key={f.key}
                label={`${f.label}${f.required ? ' *' : ''}`}
                type={f.type === 'number' ? 'number' : 'text'}
                value={String(values[f.key] ?? '')}
                onValueChange={(v) => set(f.key, v)}
                classNames={inputClasses}
              />
            );
          })}
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onPress={onClose}>Abbrechen</Button>
          <Button
            isLoading={saving}
            isDisabled={missingRequired}
            className="bg-[#3fced1] font-semibold text-black"
            onPress={handleSave}
          >
            Speichern
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

// =====================================================================
// Generische Item-Liste (mit optional Toggle + Reorder)
// =====================================================================

interface ItemListProps<T extends { id: string }> {
  items: T[];
  emptyText: string;
  renderTitle: (item: T) => React.ReactNode;
  renderSubtitle?: (item: T) => React.ReactNode;
  imageOf?: (item: T) => string | null | undefined;
  activeOf?: (item: T) => boolean;
  onToggle?: (item: T) => void;
  onMove?: (item: T, direction: -1 | 1) => void;
  onEdit: (item: T) => void;
  onDelete: (item: T) => void;
  confirmDeleteId: string | null;
}

function ItemList<T extends { id: string }>({
  items, emptyText, renderTitle, renderSubtitle, imageOf, activeOf,
  onToggle, onMove, onEdit, onDelete, confirmDeleteId,
}: ItemListProps<T>) {
  if (items.length === 0) {
    return <p className="py-8 text-center text-sm text-gray-500">{emptyText}</p>;
  }
  return (
    <div className="space-y-2">
      {items.map((item, idx) => {
        const preview = imageOf ? adminPredictionsService.buildFileUrl(imageOf(item)) : null;
        const active = activeOf ? activeOf(item) : true;
        return (
          <div
            key={item.id}
            className={`flex flex-wrap items-center gap-3 rounded-xl border px-3 py-2.5 ${
              active ? 'border-zinc-800 bg-zinc-950/60' : 'border-zinc-800 bg-zinc-950/30 opacity-60'
            }`}
          >
            {imageOf && (
              <div className="flex h-12 w-20 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900">
                {preview ? (
                  <img src={preview} alt="" className="h-full w-full object-cover" />
                ) : (
                  <ImageIcon size={15} className="text-zinc-700" />
                )}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-white">{renderTitle(item)}</div>
              {renderSubtitle && (
                <div className="truncate text-xs text-gray-500">{renderSubtitle(item)}</div>
              )}
            </div>
            <div className="flex items-center gap-1">
              {onMove && (
                <>
                  <Button isIconOnly size="sm" variant="light" aria-label="Nach oben"
                    isDisabled={idx === 0} className="text-gray-500"
                    onPress={() => onMove(item, -1)}>
                    <ArrowUp size={14} />
                  </Button>
                  <Button isIconOnly size="sm" variant="light" aria-label="Nach unten"
                    isDisabled={idx === items.length - 1} className="text-gray-500"
                    onPress={() => onMove(item, 1)}>
                    <ArrowDown size={14} />
                  </Button>
                </>
              )}
              {onToggle && (
                <Switch size="sm" isSelected={active} onValueChange={() => onToggle(item)} aria-label="Aktiv" />
              )}
              <Button isIconOnly size="sm" variant="light" aria-label="Bearbeiten"
                className="text-gray-400" onPress={() => onEdit(item)}>
                <Pencil size={14} />
              </Button>
              <Button isIconOnly size="sm" variant="light" aria-label="Löschen"
                className={confirmDeleteId === item.id ? 'text-red-400' : 'text-gray-500 hover:text-red-400'}
                onPress={() => onDelete(item)}>
                <Trash2 size={14} />
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// =====================================================================
// Seite
// =====================================================================

const AdminPredictionGameDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [game, setGame] = useState<AdminPredictionGame | null>(null);
  const [participants, setParticipants] = useState<AdminParticipant[]>([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>('details');

  // Details-Formular
  const [form, setForm] = useState<Record<string, any>>({});
  const [saving, setSaving] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [evaluating, setEvaluating] = useState(false);
  const [confirmEvaluate, setConfirmEvaluate] = useState(false);
  const [confirmDeleteGame, setConfirmDeleteGame] = useState(false);

  // Item-Verwaltung
  const [itemModal, setItemModal] = useState<{
    kind: 'slide' | 'prize' | 'lottery' | 'sponsor';
    editing: Record<string, any> | null;
  } | null>(null);
  const [confirmDeleteItemId, setConfirmDeleteItemId] = useState<string | null>(null);

  const gameIdStr = id ?? '';

  const applyGame = useCallback((g: AdminPredictionGame | null) => {
    setGame(g);
    if (g) {
      setForm({
        name: g.name ?? '',
        slug: g.slug ?? '',
        description: g.description ?? '',
        termsUrl: g.termsUrl ?? '',
        registrationEnd: toLocalInput(g.registrationEnd),
        gameStart: toLocalInput(g.gameStart),
        gameEnd: toLocalInput(g.gameEnd),
        matchPredictionsEnabled: g.settings?.matchPredictionsEnabled !== false,
        matchDeadlineHours: g.settings?.matchDeadlineHours ?? 1,
        exactScore: g.settings?.points?.exactScore ?? 3,
        correctWinner: g.settings?.points?.correctWinner ?? 1,
        semiFinalEnabled: g.settings?.specialPredictions?.semiFinalEnabled !== false,
        semiFinalPoints: g.settings?.specialPredictions?.semiFinalPoints ?? 5,
        semiFinalDeadline: toLocalInput(g.settings?.specialPredictions?.semiFinalDeadline),
        championEnabled: g.settings?.specialPredictions?.championEnabled !== false,
        championPoints: g.settings?.specialPredictions?.championPoints ?? 10,
        championDeadline: toLocalInput(g.settings?.specialPredictions?.championDeadline),
        randomizeSponsors: g.randomizeSponsors === true,
        adminOnly: g.adminOnly === true,
      });
    }
  }, []);

  const load = useCallback(async () => {
    if (!gameIdStr) return;
    try {
      const [g, parts] = await Promise.all([
        adminPredictionsService.getGame(gameIdStr),
        adminPredictionsService.getParticipants(gameIdStr).catch(() => []),
      ]);
      applyGame(g);
      setParticipants(parts);
      if (!g) setError('Tippspiel nicht gefunden.');
    } catch (err) {
      setError(adminPredictionsService.getErrorMessage(err, 'Tippspiel konnte nicht geladen werden.'));
    } finally {
      setLoading(false);
    }
  }, [gameIdStr, applyGame]);

  useEffect(() => {
    load();
  }, [load]);

  const setF = (key: string, value: any) => setForm((f) => ({ ...f, [key]: value }));

  const reloadGame = async () => {
    const g = await adminPredictionsService.getGame(gameIdStr);
    applyGame(g);
  };

  // ---------- Details speichern ----------
  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const updated = await adminPredictionsService.updateGame(gameIdStr, {
        name: String(form.name ?? '').trim(),
        slug: String(form.slug ?? '').trim() || undefined,
        description: String(form.description ?? ''),
        termsUrl: String(form.termsUrl ?? '').trim() || null,
        registrationEnd: fromLocalInput(String(form.registrationEnd ?? '')),
        gameStart: fromLocalInput(String(form.gameStart ?? '')),
        gameEnd: fromLocalInput(String(form.gameEnd ?? '')),
        randomizeSponsors: form.randomizeSponsors === true,
        adminOnly: form.adminOnly === true,
        settings: {
          matchPredictionsEnabled: form.matchPredictionsEnabled === true,
          // 0h explizit erlauben (Deadline = Spielbeginn). `|| 1` würde 0
          // fälschlich auf 1 zurücksetzen, da 0 in JS falsy ist.
          matchDeadlineHours: (() => {
            const n = parseInt(String(form.matchDeadlineHours), 10);
            return Number.isFinite(n) && n >= 0 ? n : 1;
          })(),
          points: {
            exactScore: parseInt(String(form.exactScore), 10) || 3,
            correctWinner: parseInt(String(form.correctWinner), 10) || 1,
          },
          specialPredictions: {
            semiFinalEnabled: form.semiFinalEnabled === true,
            semiFinalPoints: parseInt(String(form.semiFinalPoints), 10) || 5,
            semiFinalDeadline: fromLocalInput(String(form.semiFinalDeadline ?? '')),
            championEnabled: form.championEnabled === true,
            championPoints: parseInt(String(form.championPoints), 10) || 10,
            championDeadline: fromLocalInput(String(form.championDeadline ?? '')),
          },
        },
      } as Partial<AdminPredictionGame>);
      applyGame(updated);
      setFeedback('Änderungen gespeichert.');
    } catch (err) {
      setError(adminPredictionsService.getErrorMessage(err, 'Speichern fehlgeschlagen.'));
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (status: string) => {
    if (!status || status === game?.status) return;
    setStatusBusy(true);
    setError(null);
    try {
      const updated = await adminPredictionsService.updateStatus(gameIdStr, status);
      applyGame(updated);
      setFeedback(`Status geändert: ${adminPredictionsService.statusLabel(status)}`);
    } catch (err) {
      setError(adminPredictionsService.getErrorMessage(err, 'Status konnte nicht geändert werden.'));
    } finally {
      setStatusBusy(false);
    }
  };

  const handleEvaluate = async () => {
    if (!confirmEvaluate) {
      setConfirmEvaluate(true);
      return;
    }
    setConfirmEvaluate(false);
    setEvaluating(true);
    setError(null);
    try {
      const message = await adminPredictionsService.evaluateGame(gameIdStr);
      setFeedback(`${message} – Punkte werden im Hintergrund berechnet.`);
    } catch (err) {
      setError(adminPredictionsService.getErrorMessage(err, 'Auswertung fehlgeschlagen.'));
    } finally {
      setEvaluating(false);
    }
  };

  const handleDeleteGame = async () => {
    if (!confirmDeleteGame) {
      setConfirmDeleteGame(true);
      return;
    }
    try {
      await adminPredictionsService.deleteGame(gameIdStr);
      navigate('/admin/tippspiele');
    } catch (err) {
      setError(adminPredictionsService.getErrorMessage(err, 'Löschen fehlgeschlagen.'));
      setConfirmDeleteGame(false);
    }
  };

  // ---------- Item-Verwaltung (Slides/Preise/Lospreise/Sponsoren) ----------

  const ITEM_CONFIG: Record<string, { title: string; fields: ItemField[] }> = {
    slide: {
      title: 'Hero-Slide',
      fields: [
        { key: 'image', label: 'Bild', type: 'image', required: true },
        { key: 'title', label: 'Titel', type: 'text' },
        { key: 'subtitle', label: 'Untertitel', type: 'text' },
        { key: 'linkUrl', label: 'Link-URL', type: 'text' },
        { key: 'buttonText', label: 'Button-Text', type: 'text' },
        { key: 'ratio', label: 'Seitenverhältnis (z.B. 16:9)', type: 'text' },
      ],
    },
    prize: {
      title: 'Preis',
      fields: [
        { key: 'rank', label: 'Platz (1, 2, 3 …)', type: 'number', required: true },
        { key: 'title', label: 'Titel', type: 'text', required: true },
        { key: 'subtitle', label: 'Untertitel', type: 'text' },
        { key: 'description', label: 'Beschreibung', type: 'textarea' },
        { key: 'image', label: 'Bild', type: 'image' },
      ],
    },
    lottery: {
      title: 'Lospreis (Verlosung)',
      fields: [
        { key: 'title', label: 'Titel', type: 'text', required: true },
        { key: 'subtitle', label: 'Untertitel', type: 'text' },
        { key: 'description', label: 'Beschreibung', type: 'textarea' },
        { key: 'image', label: 'Bild', type: 'image' },
      ],
    },
    sponsor: {
      title: 'Sponsor',
      fields: [
        { key: 'title', label: 'Titel', type: 'text', required: true },
        { key: 'subtitle', label: 'Untertitel', type: 'text' },
        { key: 'description', label: 'Beschreibung', type: 'textarea' },
        { key: 'image', label: 'Banner-Bild', type: 'image' },
        { key: 'linkUrl', label: 'Link-URL', type: 'text' },
      ],
    },
  };

  const saveItem = async (kind: string, values: Record<string, any>, editingId?: string) => {
    if (kind === 'slide') {
      if (editingId) await adminPredictionsService.updateHeroSlide(gameIdStr, editingId, values);
      else await adminPredictionsService.addHeroSlide(gameIdStr, values as any);
    } else if (kind === 'prize') {
      if (editingId) await adminPredictionsService.updatePrize(gameIdStr, editingId, values);
      else await adminPredictionsService.addPrize(gameIdStr, values as any);
    } else if (kind === 'lottery') {
      if (editingId) await adminPredictionsService.updateLotteryPrize(gameIdStr, editingId, values);
      else await adminPredictionsService.addLotteryPrize(gameIdStr, values as any);
    } else if (kind === 'sponsor') {
      if (editingId) await adminPredictionsService.updateSponsor(gameIdStr, editingId, values);
      else await adminPredictionsService.addSponsor(gameIdStr, values as any);
    }
    await reloadGame();
  };

  const deleteItem = async (kind: 'slide' | 'prize' | 'lottery' | 'sponsor', itemId: string) => {
    if (confirmDeleteItemId !== itemId) {
      setConfirmDeleteItemId(itemId);
      return;
    }
    setConfirmDeleteItemId(null);
    try {
      if (kind === 'slide') await adminPredictionsService.deleteHeroSlide(gameIdStr, itemId);
      else if (kind === 'prize') await adminPredictionsService.deletePrize(gameIdStr, itemId);
      else if (kind === 'lottery') await adminPredictionsService.deleteLotteryPrize(gameIdStr, itemId);
      else await adminPredictionsService.deleteSponsor(gameIdStr, itemId);
      await reloadGame();
    } catch (err) {
      setError(adminPredictionsService.getErrorMessage(err, 'Löschen fehlgeschlagen.'));
    }
  };

  const moveItem = async (kind: 'slide' | 'sponsor', itemId: string, direction: -1 | 1) => {
    const list = kind === 'slide'
      ? [...(game?.heroSlides ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      : [...(game?.sponsors ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const idx = list.findIndex((s) => s.id === itemId);
    const target = idx + direction;
    if (idx < 0 || target < 0 || target >= list.length) return;
    const ids = list.map((s) => s.id);
    [ids[idx], ids[target]] = [ids[target], ids[idx]];
    try {
      if (kind === 'slide') await adminPredictionsService.reorderHeroSlides(gameIdStr, ids);
      else await adminPredictionsService.reorderSponsors(gameIdStr, ids);
      await reloadGame();
    } catch (err) {
      setError(adminPredictionsService.getErrorMessage(err, 'Reihenfolge konnte nicht geändert werden.'));
    }
  };

  const toggleItem = async (kind: 'slide' | 'sponsor', itemId: string) => {
    try {
      if (kind === 'slide') await adminPredictionsService.toggleHeroSlide(gameIdStr, itemId);
      else await adminPredictionsService.toggleSponsor(gameIdStr, itemId);
      await reloadGame();
    } catch (err) {
      setError(adminPredictionsService.getErrorMessage(err, 'Umschalten fehlgeschlagen.'));
    }
  };

  // ---------- Render ----------

  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <Spinner size="lg" color="primary" />
      </div>
    );
  }

  if (!game) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 text-center">
        <Dices size={40} className="mx-auto mb-3 text-zinc-700" />
        <p className="text-gray-400">{error || 'Tippspiel nicht gefunden.'}</p>
        <Button as={RouterLink} to="/admin/tippspiele" className="mt-4 bg-[#3fced1] font-semibold text-black">
          Zurück zur Übersicht
        </Button>
      </div>
    );
  }

  const slides = [...(game.heroSlides ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const prizes = [...(game.prizes ?? [])].sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0));
  const lotteryPrizes = game.lotteryPrizes ?? [];
  const sponsors = [...(game.sponsors ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  return (
    <>
      <Helmet>
        <title>{game.name} | Tippspiel-Admin | DartCloud</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        {/* Kopf */}
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <Button
            as={RouterLink}
            to="/admin/tippspiele"
            isIconOnly
            variant="bordered"
            className="border-zinc-700 text-gray-300"
            aria-label="Zurück zur Tippspiel-Übersicht"
          >
            <ArrowLeft size={18} />
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="flex flex-wrap items-center gap-2 text-xl font-extrabold tracking-tight text-white sm:text-2xl">
              {game.name}
              {game.adminOnly && (
                <Chip size="sm" variant="flat" className="bg-purple-500/15 text-purple-400" startContent={<Shield size={11} />}>
                  Nur Admins
                </Chip>
              )}
            </h1>
            <p className="flex flex-wrap items-center gap-x-3 text-xs text-gray-500">
              <span className="inline-flex items-center gap-1"><Trophy size={12} /> {game.tournament?.name || '–'}</span>
              <span className="inline-flex items-center gap-1"><Users size={12} /> {game.stats?.totalParticipants ?? participants.length} Teilnehmer</span>
            </p>
          </div>
          {game.slug && (
            <Button
              as={RouterLink}
              to={`/tippspiele/${game.slug}`}
              size="sm"
              variant="bordered"
              className="border-zinc-700 text-gray-300"
              endContent={<ExternalLink size={13} />}
            >
              Ansehen
            </Button>
          )}
        </div>

        <FeedbackBanner message={feedback} type="success" onClose={() => setFeedback(null)} />
        <FeedbackBanner message={error} type="error" onClose={() => setError(null)} />

        {/* Eigene Tab-Leiste (NextUI-Tabs renderten fehlerhaft – ein Tab in voller Breite) */}
        <div className="-mx-4 flex gap-2 overflow-x-auto border-b border-zinc-800 px-4 pb-3 sm:mx-0 sm:px-0 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {([
            { key: 'details', label: 'Details' },
            { key: 'participants', label: `Teilnehmer (${participants.length})` },
            { key: 'slides', label: `Slides (${slides.length})` },
            { key: 'prizes', label: `Preise (${prizes.length})` },
            { key: 'lottery', label: `Lospreise (${lotteryPrizes.length})` },
            { key: 'sponsors', label: `Sponsoren (${sponsors.length})` },
          ] as { key: TabKey; label: string }[]).map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setActiveTab(t.key)}
              className={`shrink-0 whitespace-nowrap rounded-full border px-3.5 py-1.5 text-sm font-semibold transition-colors ${
                activeTab === t.key
                  ? 'border-transparent bg-[#3fced1] text-black'
                  : 'border-zinc-700 text-gray-300 hover:border-zinc-500'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* ================= DETAILS ================= */}
        {activeTab === 'details' && (
            <div className="space-y-4 pt-5">
              {/* Status + Aktionen */}
              <Card className="border border-zinc-800 bg-zinc-900">
                <CardBody className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center">
                  <Select
                    label="Status"
                    selectedKeys={[game.status ?? 'draft']}
                    isDisabled={statusBusy}
                    onSelectionChange={(keys) => handleStatusChange(String(Array.from(keys)[0] ?? ''))}
                    className="sm:max-w-[220px]"
                    classNames={{ trigger: 'bg-zinc-800 border border-zinc-700', label: 'text-gray-400', value: 'text-white' }}
                  >
                    <SelectItem key="draft">Entwurf</SelectItem>
                    <SelectItem key="open">Anmeldung offen</SelectItem>
                    <SelectItem key="running">Läuft</SelectItem>
                    <SelectItem key="finished">Beendet</SelectItem>
                  </Select>
                  <div className="flex flex-1 flex-wrap items-center justify-end gap-2">
                    <div className="mr-auto flex gap-4 text-center text-xs text-gray-400">
                      <span><strong className="block text-base text-white">{game.stats?.totalParticipants ?? 0}</strong>Teilnehmer</span>
                      <span><strong className="block text-base text-white">{game.stats?.totalPredictions ?? 0}</strong>Tipps</span>
                      <span><strong className="block text-base text-white">{game.stats?.evaluatedMatches ?? 0}</strong>Ausgewertet</span>
                    </div>
                    <Button
                      variant="bordered"
                      className={confirmEvaluate ? 'border-amber-500 text-amber-400' : 'border-zinc-700 text-gray-300'}
                      isLoading={evaluating}
                      startContent={!evaluating && <Calculator size={15} />}
                      onPress={handleEvaluate}
                    >
                      {confirmEvaluate ? 'Wirklich auswerten?' : 'Jetzt auswerten'}
                    </Button>
                    <Button
                      variant="bordered"
                      className={confirmDeleteGame ? 'border-red-500 text-red-400' : 'border-zinc-700 text-gray-400'}
                      startContent={<Trash2 size={15} />}
                      onPress={handleDeleteGame}
                    >
                      {confirmDeleteGame ? 'Wirklich löschen?' : 'Löschen'}
                    </Button>
                  </div>
                </CardBody>
              </Card>

              {/* Stammdaten */}
              <Card className="border border-zinc-800 bg-zinc-900">
                <CardBody className="grid gap-4 p-4 sm:grid-cols-2">
                  <Input label="Name" value={String(form.name ?? '')} onValueChange={(v) => setF('name', v)} classNames={inputClasses} />
                  <Input label="Slug (URL)" value={String(form.slug ?? '')} onValueChange={(v) => setF('slug', v)} classNames={inputClasses} />
                  <Textarea label="Beschreibung" value={String(form.description ?? '')} onValueChange={(v) => setF('description', v)} minRows={3} className="sm:col-span-2" classNames={inputClasses} />
                  <Input label="Tippspielbedingungen-URL" value={String(form.termsUrl ?? '')} onValueChange={(v) => setF('termsUrl', v)} className="sm:col-span-2" classNames={inputClasses} />
                  <Input label="Anmeldeschluss" type="datetime-local" value={String(form.registrationEnd ?? '')} onValueChange={(v) => setF('registrationEnd', v)} classNames={inputClasses} />
                  <Input label="Spielstart" type="datetime-local" value={String(form.gameStart ?? '')} onValueChange={(v) => setF('gameStart', v)} classNames={inputClasses} />
                  <Input label="Spielende" type="datetime-local" value={String(form.gameEnd ?? '')} onValueChange={(v) => setF('gameEnd', v)} classNames={inputClasses} />
                </CardBody>
              </Card>

              {/* Punkte & Specials */}
              <Card className="border border-zinc-800 bg-zinc-900">
                <CardBody className="space-y-4 p-4">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-sm font-semibold text-white">Match-Tipps</p>
                      <p className="text-xs text-gray-400">Tipps auf einzelne Spiele des Turniers.</p>
                    </div>
                    <Switch isSelected={form.matchPredictionsEnabled === true} onValueChange={(v) => setF('matchPredictionsEnabled', v)} aria-label="Match-Tipps" />
                  </div>
                  <div className="grid gap-4 sm:grid-cols-3">
                    <Input label="Deadline (Std. vor Spielbeginn)" type="number" min={0} value={String(form.matchDeadlineHours ?? '')} onValueChange={(v) => setF('matchDeadlineHours', v)} classNames={inputClasses} />
                    <Input label="Punkte: exaktes Ergebnis" type="number" value={String(form.exactScore ?? '')} onValueChange={(v) => setF('exactScore', v)} classNames={inputClasses} />
                    <Input label="Punkte: richtiger Sieger" type="number" value={String(form.correctWinner ?? '')} onValueChange={(v) => setF('correctWinner', v)} classNames={inputClasses} />
                  </div>

                  <div className="border-t border-zinc-800 pt-4">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-semibold text-white">Halbfinal-Tipp</p>
                        <p className="text-xs text-gray-400">Wer erreicht das Halbfinale?</p>
                      </div>
                      <Switch isSelected={form.semiFinalEnabled === true} onValueChange={(v) => setF('semiFinalEnabled', v)} aria-label="Halbfinal-Tipp" />
                    </div>
                    <div className="mt-3 grid gap-4 sm:grid-cols-2">
                      <Input label="Punkte pro richtigem Halbfinalisten" type="number" value={String(form.semiFinalPoints ?? '')} onValueChange={(v) => setF('semiFinalPoints', v)} classNames={inputClasses} />
                      <Input label="Deadline Halbfinal-Tipp" type="datetime-local" value={String(form.semiFinalDeadline ?? '')} onValueChange={(v) => setF('semiFinalDeadline', v)} classNames={inputClasses} />
                    </div>
                  </div>

                  <div className="border-t border-zinc-800 pt-4">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-semibold text-white">Champion-Tipp</p>
                        <p className="text-xs text-gray-400">Wer gewinnt das Turnier?</p>
                      </div>
                      <Switch isSelected={form.championEnabled === true} onValueChange={(v) => setF('championEnabled', v)} aria-label="Champion-Tipp" />
                    </div>
                    <div className="mt-3 grid gap-4 sm:grid-cols-2">
                      <Input label="Punkte für richtigen Champion" type="number" value={String(form.championPoints ?? '')} onValueChange={(v) => setF('championPoints', v)} classNames={inputClasses} />
                      <Input label="Deadline Champion-Tipp" type="datetime-local" value={String(form.championDeadline ?? '')} onValueChange={(v) => setF('championDeadline', v)} classNames={inputClasses} />
                    </div>
                  </div>
                </CardBody>
              </Card>

              {/* Schalter */}
              <Card className="border border-zinc-800 bg-zinc-900">
                <CardBody className="space-y-4 p-4">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="flex items-center gap-1.5 text-sm font-semibold text-white">
                        <Shield size={14} className="text-purple-400" /> Nur für Admins
                      </p>
                      <p className="text-xs text-gray-400">
                        Nur Admins sehen dieses Tippspiel (Listen, Startseite, App) und können sich anmelden.
                      </p>
                    </div>
                    <Switch isSelected={form.adminOnly === true} onValueChange={(v) => setF('adminOnly', v)} aria-label="Nur für Admins" />
                  </div>
                  <div className="flex items-center justify-between gap-4 border-t border-zinc-800 pt-4">
                    <div>
                      <p className="text-sm font-semibold text-white">Sponsoren zufällig sortieren</p>
                      <p className="text-xs text-gray-400">Sponsoren-Banner in zufälliger Reihenfolge anzeigen.</p>
                    </div>
                    <Switch isSelected={form.randomizeSponsors === true} onValueChange={(v) => setF('randomizeSponsors', v)} aria-label="Sponsoren zufällig" />
                  </div>
                </CardBody>
              </Card>

              <div className="flex justify-end">
                <Button
                  className="bg-[#3fced1] font-semibold text-black"
                  isLoading={saving}
                  startContent={!saving && <Save size={15} />}
                  onPress={handleSave}
                >
                  Speichern
                </Button>
              </div>
            </div>
        )}

        {/* ================= TEILNEHMER ================= */}
        {activeTab === 'participants' && (
            <div className="pt-5">
              {participants.length === 0 ? (
                <p className="py-10 text-center text-sm text-gray-500">Noch keine Teilnehmer.</p>
              ) : (
                <div className="overflow-hidden rounded-xl border border-zinc-800">
                  <table className="w-full text-sm">
                    <thead className="bg-zinc-950/80">
                      <tr className="text-left">
                        <th className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-widest text-gray-400">#</th>
                        <th className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-widest text-gray-400">Tippname</th>
                        <th className="hidden px-4 py-2.5 text-[11px] font-semibold uppercase tracking-widest text-gray-400 sm:table-cell">User</th>
                        <th className="px-4 py-2.5 text-right text-[11px] font-semibold uppercase tracking-widest text-gray-400">Punkte</th>
                        <th className="hidden px-4 py-2.5 text-right text-[11px] font-semibold uppercase tracking-widest text-gray-400 md:table-cell">Angemeldet</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...participants]
                        .sort((a, b) => (b.totalPoints ?? 0) - (a.totalPoints ?? 0))
                        .map((p, idx) => (
                          <tr key={p._id} className="border-t border-zinc-800 hover:bg-zinc-900/60">
                            <td className="px-4 py-2.5 text-gray-500">{idx + 1}</td>
                            <td className="px-4 py-2.5 font-semibold text-white">{p.predictionUsername || '–'}</td>
                            <td className="hidden px-4 py-2.5 text-gray-400 sm:table-cell">
                              {p.user?.username || p.user?.email || '–'}
                            </td>
                            <td className="px-4 py-2.5 text-right font-bold text-[#3fced1]">{p.totalPoints ?? 0}</td>
                            <td className="hidden px-4 py-2.5 text-right text-gray-500 md:table-cell">{formatDateTime(p.registeredAt)}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
        )}

        {/* ================= HERO SLIDES ================= */}
        {activeTab === 'slides' && (
            <div className="space-y-4 pt-5">
              <div className="flex justify-end">
                <Button className="bg-[#3fced1] font-semibold text-black" startContent={<Plus size={15} />}
                  onPress={() => setItemModal({ kind: 'slide', editing: null })}>
                  Slide hinzufügen
                </Button>
              </div>
              <ItemList
                items={slides}
                emptyText="Noch keine Hero-Slides."
                renderTitle={(s: AdminHeroSlide) => s.title || '(ohne Titel)'}
                renderSubtitle={(s: AdminHeroSlide) => [s.subtitle, s.ratio].filter(Boolean).join(' · ')}
                imageOf={(s: AdminHeroSlide) => s.image}
                activeOf={(s: AdminHeroSlide) => s.active !== false}
                onToggle={(s) => toggleItem('slide', s.id)}
                onMove={(s, dir) => moveItem('slide', s.id, dir)}
                onEdit={(s) => setItemModal({ kind: 'slide', editing: s as any })}
                onDelete={(s) => deleteItem('slide', s.id)}
                confirmDeleteId={confirmDeleteItemId}
              />
            </div>
        )}

        {/* ================= PREISE ================= */}
        {activeTab === 'prizes' && (
            <div className="space-y-4 pt-5">
              <div className="flex justify-end">
                <Button className="bg-[#3fced1] font-semibold text-black" startContent={<Plus size={15} />}
                  onPress={() => setItemModal({ kind: 'prize', editing: null })}>
                  Preis hinzufügen
                </Button>
              </div>
              <ItemList
                items={prizes}
                emptyText="Noch keine Preise für die Bestenliste."
                renderTitle={(p: AdminPrize) => (
                  <span><span className="mr-1.5 text-[#3fced1]">Platz {p.rank}:</span>{p.title}</span>
                )}
                renderSubtitle={(p: AdminPrize) => p.subtitle || p.description || ''}
                imageOf={(p: AdminPrize) => p.image}
                onEdit={(p) => setItemModal({ kind: 'prize', editing: p as any })}
                onDelete={(p) => deleteItem('prize', p.id)}
                confirmDeleteId={confirmDeleteItemId}
              />
            </div>
        )}

        {/* ================= LOSPREISE ================= */}
        {activeTab === 'lottery' && (
            <div className="space-y-4 pt-5">
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-gray-500">
                  <Ticket size={13} className="mr-1 -mt-0.5 inline" />
                  Werden am Ende unter allen Teilnehmern verlost – unabhängig von der Platzierung.
                </p>
                <Button className="shrink-0 bg-[#3fced1] font-semibold text-black" startContent={<Plus size={15} />}
                  onPress={() => setItemModal({ kind: 'lottery', editing: null })}>
                  Lospreis hinzufügen
                </Button>
              </div>
              <ItemList
                items={lotteryPrizes}
                emptyText="Noch keine Lospreise."
                renderTitle={(p: AdminLotteryPrize) => p.title}
                renderSubtitle={(p: AdminLotteryPrize) => p.subtitle || p.description || ''}
                imageOf={(p: AdminLotteryPrize) => p.image}
                onEdit={(p) => setItemModal({ kind: 'lottery', editing: p as any })}
                onDelete={(p) => deleteItem('lottery', p.id)}
                confirmDeleteId={confirmDeleteItemId}
              />
            </div>
        )}

        {/* ================= SPONSOREN ================= */}
        {activeTab === 'sponsors' && (
            <div className="space-y-4 pt-5">
              <div className="flex justify-end">
                <Button className="bg-[#3fced1] font-semibold text-black" startContent={<Plus size={15} />}
                  onPress={() => setItemModal({ kind: 'sponsor', editing: null })}>
                  Sponsor hinzufügen
                </Button>
              </div>
              <ItemList
                items={sponsors}
                emptyText="Noch keine Sponsoren."
                renderTitle={(s: AdminSponsor) => s.title}
                renderSubtitle={(s: AdminSponsor) => s.linkUrl || s.subtitle || ''}
                imageOf={(s: AdminSponsor) => s.image}
                activeOf={(s: AdminSponsor) => s.active !== false}
                onToggle={(s) => toggleItem('sponsor', s.id)}
                onMove={(s, dir) => moveItem('sponsor', s.id, dir)}
                onEdit={(s) => setItemModal({ kind: 'sponsor', editing: s as any })}
                onDelete={(s) => deleteItem('sponsor', s.id)}
                confirmDeleteId={confirmDeleteItemId}
              />
            </div>
        )}
      </div>

      {/* Item-Modal (Anlegen/Bearbeiten) */}
      {itemModal && (
        <ItemModal
          isOpen
          title={`${ITEM_CONFIG[itemModal.kind].title} ${itemModal.editing ? 'bearbeiten' : 'hinzufügen'}`}
          fields={ITEM_CONFIG[itemModal.kind].fields}
          initial={itemModal.editing}
          onClose={() => setItemModal(null)}
          onSave={(values) => saveItem(itemModal.kind, values, itemModal.editing?.id)}
          onError={setError}
        />
      )}
    </>
  );
};

export default AdminPredictionGameDetailPage;
